import { useCallback, useEffect, useRef, useState } from "react";
import { PROMO_SLIDES } from "@/data/content";

const getIsMobile = () => typeof window !== "undefined" && window.innerWidth < 768;

export const PromoSlider = () => {
  const [isMobile, setIsMobile] = useState(getIsMobile);
  const [index, setIndex] = useState(0);
  const timer = useRef(null);

  // Desktop excludes mobile-only slides (e.g. YOXO until its desktop visual arrives)
  const slides = PROMO_SLIDES.filter((s) => (isMobile ? true : !s.mobileOnly));
  const count = slides.length;

  useEffect(() => {
    const onResize = () => setIsMobile(getIsMobile());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Keep index within range when the slide set changes (mobile <-> desktop)
  useEffect(() => {
    setIndex((p) => (p >= count ? 0 : p));
  }, [count]);

  const go = useCallback((i) => setIndex((i + count) % count), [count]);
  const next = useCallback(() => setIndex((p) => (p + 1) % count), [count]);
  const prev = useCallback(() => setIndex((p) => (p - 1 + count) % count), [count]);

  useEffect(() => {
    timer.current = setInterval(next, 5000);
    return () => clearInterval(timer.current);
  }, [next]);

  const pause = () => clearInterval(timer.current);
  const resume = () => {
    clearInterval(timer.current);
    timer.current = setInterval(next, 5000);
  };

  const touchStartX = useRef(null);
  const onTouchStart = (e) => {
    pause();
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStartX.current !== null) {
      const delta = e.changedTouches[0].clientX - touchStartX.current;
      if (Math.abs(delta) > 40) (delta < 0 ? next : prev)();
      touchStartX.current = null;
    }
    resume();
  };

  return (
    <section className="bg-[#f6f9fc] py-12 md:py-16" data-testid="promo-slider-section">
      <div className="mx-auto w-full max-w-[1000px] px-0 md:px-6">
        <div
          className="relative overflow-hidden rounded-none bg-white shadow-[0_24px_60px_-24px_rgba(20,60,120,0.4)] ring-1 ring-slate-100 md:rounded-2xl"
          style={{ aspectRatio: isMobile ? "700 / 400" : "1000 / 350", touchAction: "pan-y" }}
          onMouseEnter={pause}
          onMouseLeave={resume}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {slides.map((slide, i) => (
            <a
              key={slide.alt}
              href={slide.link}
              target="_blank"
              rel="noopener noreferrer"
              aria-hidden={i !== index}
              tabIndex={i === index ? 0 : -1}
              className="absolute inset-0 transition-opacity duration-500 ease-in-out"
              style={{ opacity: i === index ? 1 : 0, pointerEvents: i === index ? "auto" : "none" }}
              data-testid={`promo-slide-${i}`}
            >
              <img
                src={isMobile ? slide.imgMobile : slide.img}
                alt={slide.alt}
                className="h-full w-full object-cover"
              />
            </a>
          ))}

          {/* Arrows (desktop only) */}
          <button
            onClick={prev}
            aria-label="Slide anterior"
            className="isic-slider-arrow absolute left-3 top-1/2 -translate-y-1/2 !hidden md:!flex"
            data-testid="promo-prev"
          >
            <svg width="26" height="26" viewBox="0 0 32 32"><path d="M11.433 15.992L22.69 5.712c.393-.39.393-1.03 0-1.42-.393-.39-1.03-.39-1.423 0l-11.98 10.94c-.21.21-.3.49-.285.76-.015.28.075.56.284.77l11.98 10.94c.393.39 1.03.39 1.424 0 .393-.4.393-1.03 0-1.42l-11.257-10.29" fill="#ffffff"/></svg>
          </button>
          <button
            onClick={next}
            aria-label="Slide următor"
            className="isic-slider-arrow absolute right-3 top-1/2 -translate-y-1/2 !hidden md:!flex"
            data-testid="promo-next"
          >
            <svg width="26" height="26" viewBox="0 0 32 32"><path d="M10.722 4.293c-.394-.39-1.032-.39-1.427 0-.393.39-.393 1.03 0 1.42l11.283 10.28-11.283 10.29c-.393.39-.393 1.02 0 1.42.395.39 1.033.39 1.427 0l12.007-10.94c.21-.21.3-.49.284-.77.014-.27-.076-.55-.286-.76L10.72 4.293z" fill="#ffffff"/></svg>
          </button>

          {/* Dots */}
          <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-2">
            {slides.map((slide, i) => (
              <button
                key={slide.alt}
                onClick={() => go(i)}
                aria-label={`Slide ${i + 1}`}
                className="h-2 rounded-full transition-all duration-300"
                style={{
                  width: i === index ? "22px" : "8px",
                  backgroundColor: i === index ? "#ffffff" : "rgba(255,255,255,0.55)",
                }}
                data-testid={`promo-dot-${i}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
